/* eslint-disable react/no-unknown-property */
import React from 'react'
import {Box, Image, ImageProps} from '@chakra-ui/react'
import themeBreakpoints from '../../../theme/foundations/breakpoints'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

interface PictureProps extends ImageProps {
    alt?: string
    imagelg?: string
    imagemd?: string
    imagesm?: string
    imagexs?: string
    imagexl?: string
    positionContainer?: 'absolute' | 'relative' | 'fixed' | 'static' | 'sticky'
    position?: 'absolute' | 'relative' | 'fixed' | 'static' | 'sticky'
    zIndex?: number
    objectFit?: 'cover' | 'contain'
    width?: string | {base?: string; sm?: string; md?: string; lg?: string}
    height?: string | {base?: string; sm?: string; md?: string; lg?: string}
    overflow?: string
    isTileImage?: boolean
    isTileHorizontal?: boolean
    hasLazy?: boolean
    decoding?: 'sync' | 'async' | 'auto'
    fetchpriority?: 'high' | 'low' | 'auto'
}

/**
 * @name
 *
 * @component
 *
 * @description
 * Renders an image with responsive sources and various styling options.
 *
 * @param {PictureProps} props - Properties object.
 * @param {string} [props.alt] - The alt text for the image.
 * @param {string} [props.imagexs] - The source URL for the extra small image.
 * @param {string} [props.imagesm] - The source URL for the small image.
 * @param {string} [props.imagemd] - The source URL for the medium image.
 * @param {string} [props.imagelg] - The source URL for the large image.
 * @param {string} [props.positionContainer] - Position container property value.
 * @param {position} [props.position] - Position property value.
 * @param {number} [props.zIndex] - zIndex property value.
 * @param {string} [props.objectFit] - Object fit property value.
 * @param {string} [props.overflow='visible'] - The overflow property of the image container.
 * @param {string} [props.width='100%'] - The width of the image container.
 * @param {string} [props.height='100%'] - The height of the image container.
 * @param {boolean} [props.isTileImage] - Indicates if image is tile.
 * @param {boolean} [props.isTileHorizontal] - Indicates if tile is horizontal.
 * @param {boolean} [props.hasLazy] - Has lazy property value.
 *
 * @returns {JSX.Element} The rendered `Picture` component.
 */
const Picture: React.FC<PictureProps> = ({
    alt,
    imagexs,
    imagesm,
    imagemd,
    imagelg,
    imagexl,
    overflow = 'visible',
    width = '100%',
    height = '100%',
    isTileImage = false,
    isTileHorizontal = false,
    hasLazy = true,
    positionContainer,
    fetchpriority,
    ...props
}) => {
    const {breakpoints} = themeBreakpoints
    const imageUrl = imagexs || imagesm || imagemd || imagelg
    const loadingMode = props.loading ?? (hasLazy ? 'lazy' : 'eager')
    const decodingMode = props.decoding ?? (hasLazy ? 'async' : 'auto')
    const fetchPriorityAttr = fetchpriority ?? (hasLazy ? 'low' : 'auto')

    return (
        <Box
            overflow={overflow}
            top={0}
            width={width}
            height={height}
            {...props}
            pt={isTileImage ? (isTileHorizontal ? '63.538%' : '127.3%') : '0'}
            data-testid="picture"
            position={isTileImage ? 'relative' : (positionContainer ?? 'unset')}
        >
            {isTileImage && hasLazy && (
                <Image
                    src={getAssetUrl('static/img/product_placeholder.jpg')}
                    alt={`${alt} - Placeholder`}
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    zIndex={-1}
                />
            )}

            <picture>
                {imagexl && <source media={`(min-width: ${breakpoints.xl})`} srcSet={imagexl || imagelg} />}
                {imagelg && <source media={`(min-width: ${breakpoints.lg})`} srcSet={imagelg} />}
                {imagemd && <source media={`(min-width: ${breakpoints.md})`} srcSet={imagemd || imagelg} />}
                {imagesm && <source media={`(min-width: ${breakpoints.sm})`} srcSet={imagesm || imagemd || imagelg} />}

                <img
                    {...(imageUrl && imageUrl.includes('dpr_auto')
                        ? {
                              srcSet: `${imageUrl.replace('dpr_auto', 'dpr_1.0')} 1x, ${imageUrl.replace('dpr_auto', 'dpr_2.0')} 2x, ${imageUrl.replace('dpr_auto', 'dpr_3.0')} 3x`,
                          }
                        : {})}
                    src={imageUrl}
                    alt={alt}
                    loading={loadingMode}
                    decoding={decodingMode}
                    // @ts-expect-error fetchpriority is a valid HTML attribute, not in React types yet
                    fetchpriority={fetchPriorityAttr}
                    style={{
                        width: '100%',
                        height: props.position === 'absolute' || props.objectFit === 'cover' ? '100%' : 'auto',
                        position: props.position ?? 'unset',
                        top: props.position === 'absolute' ? 0 : 'unset',
                        left: props.position === 'absolute' ? 0 : 'unset',
                        bottom: props.position === 'absolute' ? 0 : 'unset',
                        right: props.position === 'absolute' ? 0 : 'unset',
                        zIndex: props.zIndex ?? 1,
                        objectFit: props.objectFit ?? 'unset',
                    }}
                />
            </picture>
        </Box>
    )
}

export {Picture, PictureProps}
